import React from "react";
import { Tag } from "antd";
import { formatAmount, formatHistoryDate } from "./historyUtils";
import type { AuditLog } from "./historyUtils";

interface ItemAdded {
  product_name: string;
  quantity: number;
  price: number;
}

interface ItemRemoved {
  product_name: string;
  quantity: number;
  price: number;
}

interface ItemUpdated {
  product_name: string;
  old_qty: number;
  new_qty: number;
  old_price: number;
  new_price: number;
}

interface PaymentChange {
  method: string;
  amount: number;
}

interface AuditLogEntryProps {
  log: AuditLog;
}

interface ChangeTagProps {
  color: string;
  children: React.ReactNode;
}

const CHANGE_ARROW = "\u2192";

const ChangeTag: React.FC<ChangeTagProps> = ({ color, children }) => (
  <Tag color={color} className="text-xs m-0 mr-1">
    {children}
  </Tag>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <span className="font-semibold text-gray-500 text-xs uppercase">
    {children}
  </span>
);

const hasRecords = <T,>(records?: T[]) => (records?.length ?? 0) > 0;

const ItemAddedRow: React.FC<{ item: ItemAdded }> = ({ item }) => (
  <div className="flex justify-between items-center bg-green-50 border border-green-100 rounded px-2 py-1 text-xs text-green-700">
    <span>
      <ChangeTag color="success">ADD</ChangeTag>
      {item.product_name}
    </span>
    <span>
      Qty: {item.quantity} | LKR {formatAmount(item.price)}
    </span>
  </div>
);

const ItemRemovedRow: React.FC<{ item: ItemRemoved }> = ({ item }) => (
  <div className="flex justify-between items-center bg-red-50 border border-red-100 rounded px-2 py-1 text-xs text-red-700">
    <span>
      <ChangeTag color="error">REMOVE</ChangeTag>
      {item.product_name}
    </span>
    <span>
      Qty: {item.quantity} | LKR {formatAmount(item.price)}
    </span>
  </div>
);

const ItemUpdatedRow: React.FC<{ item: ItemUpdated }> = ({ item }) => {
  const quantityChanged = item.old_qty !== item.new_qty;
  const priceChanged = item.old_price !== item.new_price;

  return (
    <div className="bg-yellow-50 border border-yellow-100 rounded px-2 py-1 text-xs text-yellow-800">
      <div>
        <ChangeTag color="warning">UPDATE</ChangeTag>
        <span className="font-semibold">{item.product_name}</span>
      </div>
      <div className="flex gap-4 mt-1 text-gray-500">
        {quantityChanged && (
          <span>
            Qty: <span className="line-through">{item.old_qty}</span>{" "}
            {CHANGE_ARROW} <strong>{item.new_qty}</strong>
          </span>
        )}
        {priceChanged && (
          <span>
            Price:{" "}
            <span className="line-through">
              LKR {formatAmount(item.old_price)}
            </span>{" "}
            {CHANGE_ARROW} <strong>LKR {formatAmount(item.new_price)}</strong>
          </span>
        )}
      </div>
    </div>
  );
};

const PaymentAddedRow: React.FC<{ payment: PaymentChange }> = ({ payment }) => (
  <div className="flex justify-between items-center bg-blue-50 border border-blue-100 rounded px-2 py-1 text-xs text-blue-700">
    <span>
      <ChangeTag color="processing">ADD</ChangeTag>
      {payment.method}
    </span>
    <span>LKR {formatAmount(payment.amount)}</span>
  </div>
);

const PaymentRemovedRow: React.FC<{ payment: PaymentChange }> = ({
  payment,
}) => (
  <div className="flex justify-between items-center bg-gray-50 border border-gray-200 rounded px-2 py-1 text-xs text-gray-500">
    <span>
      <ChangeTag color="default">REVERSE</ChangeTag>
      {payment.method}
    </span>
    <span>LKR {formatAmount(payment.amount)}</span>
  </div>
);

const StatusChangeRow: React.FC<{
  oldStatus: string;
  newStatus: string;
}> = ({ oldStatus, newStatus }) => (
  <div className="flex items-center gap-2 text-xs">
    <span className="font-semibold text-gray-600">Status:</span>
    <span className="line-through text-gray-400">{oldStatus}</span>
    <span className="text-gray-400">{CHANGE_ARROW}</span>
    <span className="font-semibold text-gray-800">{newStatus}</span>
  </div>
);

export const AuditLogEntry: React.FC<AuditLogEntryProps> = ({ log }) => {
  const changes = log.changes || {};
  const itemChanges = changes.items;
  const paymentChanges = changes.payments;
  const hasItems =
    hasRecords(itemChanges?.added) ||
    hasRecords(itemChanges?.removed) ||
    hasRecords(itemChanges?.updated);
  const hasPayments =
    hasRecords(paymentChanges?.added) || hasRecords(paymentChanges?.removed);
  const hasStatus = Boolean(changes.status);

  return (
    <div className="bg-white border border-gray-200 rounded">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-2">
          <Tag
            color={log.action === "created" ? "success" : "processing"}
            className="m-0 uppercase text-xs font-semibold"
          >
            {log.action}
          </Tag>
          <span className="text-xs text-gray-500">
            by{" "}
            <span className="font-medium text-gray-700">
              {log.changer_name || "System"}
            </span>
          </span>
        </div>
        <span className="text-xs text-gray-400">
          {formatHistoryDate(log.created_at, true)}
        </span>
      </div>

      <div className="px-3 py-2 flex flex-col gap-2 text-xs">
        {log.action === "created" && (
          <span className="text-green-600">Order created.</span>
        )}

        {hasStatus && changes.status && (
          <StatusChangeRow
            oldStatus={changes.status.old_status}
            newStatus={changes.status.new_status}
          />
        )}

        {hasItems && (
          <div className="flex flex-col gap-1">
            <SectionLabel>Items</SectionLabel>

            {itemChanges?.added?.map((item, index) => (
              <ItemAddedRow key={`add-${index}`} item={item} />
            ))}

            {itemChanges?.removed?.map((item, index) => (
              <ItemRemovedRow key={`rem-${index}`} item={item} />
            ))}

            {itemChanges?.updated?.map((item, index) => (
              <ItemUpdatedRow key={`upd-${index}`} item={item} />
            ))}
          </div>
        )}

        {hasPayments && (
          <div className="flex flex-col gap-1">
            <SectionLabel>Payments</SectionLabel>

            {paymentChanges?.added?.map((payment, index) => (
              <PaymentAddedRow key={`padd-${index}`} payment={payment} />
            ))}

            {paymentChanges?.removed?.map((payment, index) => (
              <PaymentRemovedRow key={`prem-${index}`} payment={payment} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
